import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, Copy, ExternalLink, Loader2, X } from 'lucide-react';
import apiService from '../services/api.service';
import { PUBLIC_SITE_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useEvent } from '../contexts/EventContext';
import { overviewKey } from '../hooks/useEventOverview';
import { refuseIfDateInputIncomplete } from '../lib/dateInputs';
import { cn } from '../lib/utils';
import { PageHeader } from '../components/ui/page-header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { DateTimeInput } from '../components/ui/date-time-input';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { LogoWarnings } from '../components/LogoWarnings';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';

// Paramètres d'un événement — une page, plus une modale (2026-09-23).
//
// Le formulaire était une fenêtre de ~400 lignes qui défilait dans 90 % de la
// hauteur. Une page a une adresse (`/admin/events/:id/settings`), des sections
// qu'on atteint directement, et l'espace pour les lire.
//
// ⛔ Le bouton « Enregistrer » RESTE (règle du 2026-09-22) : le statut et la
// visibilité rendent public. Et les invitations d'un événement privé partent
// par un bouton À ELLES, qui annonce combien d'emails il envoie — l'ancienne
// fenêtre les envoyait au passage, en enregistrant le reste.

interface EventDetail {
  id: string;
  name: string;
  event_type: 'crossfit' | 'course';
  description?: string | null;
  date_start?: string | null;
  date_end?: string | null;
  location_street?: string | null;
  location_postal_code?: string | null;
  location_city?: string | null;
  location_country?: string | null;
  venue_name?: string | null;
  status: string;
  visibility?: 'public' | 'private' | 'unlisted' | null;
  slug?: string | null;
  logo_url?: string | null;
}

interface Values {
  name: string;
  event_type: 'crossfit' | 'course';
  description: string;
  date_start: string;
  date_end: string;
  venue_name: string;
  location_street: string;
  location_postal_code: string;
  location_city: string;
  location_country: string;
  status: string;
  visibility: 'public' | 'private' | 'unlisted';
}

const dateOnly = (s?: string | null) => (s ? s.split('T')[0].split(' ')[0] : '');

const toValues = (e: EventDetail): Values => ({
  name: e.name || '',
  event_type: e.event_type,
  description: e.description || '',
  date_start: dateOnly(e.date_start),
  date_end: dateOnly(e.date_end || e.date_start),
  venue_name: e.venue_name || '',
  location_street: e.location_street || '',
  location_postal_code: e.location_postal_code || '',
  location_city: e.location_city || '',
  location_country: e.location_country || '',
  status: e.status,
  visibility: (e.visibility as Values['visibility']) || 'public',
});

const logoFullUrl = (url: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('/api')) {
    const base = (process.env.REACT_APP_API_URL || 'https://events.logfit.eu/api/v1').replace('/api/v1', '');
    return `${base}${url}`;
  }
  return url;
};

const SECTIONS = ['info', 'place', 'publication', 'invitations', 'logo'] as const;
type SectionId = typeof SECTIONS[number];

// Défini HORS de la page : recréé à chaque rendu, il démonterait ses champs à
// chaque frappe et leur ferait perdre le focus.
const Section: React.FC<{ id: SectionId; title: string; hint?: string; children: React.ReactNode }> = ({ id, title, hint, children }) => (
  <section id={`settings-${id}`} aria-labelledby={`settings-${id}-title`} className="scroll-mt-24 border-t border-white/[0.08] pt-6">
    <h2 id={`settings-${id}-title`} className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{title}</h2>
    {hint && <p className="mt-1 text-sm text-muted-foreground">{hint}</p>}
    <div className="mt-5 space-y-5">{children}</div>
  </section>
);

const EventSettings: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { features } = useAuth();
  const { selectedEventId, setSelectedEventId, refreshEvents } = useEvent();

  // L'adresse fait foi : arriver ici par un lien choisit cet événement dans
  // la barre latérale, sinon le menu montrerait un autre événement.
  useEffect(() => {
    if (eventId && eventId !== selectedEventId) setSelectedEventId(eventId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const query = useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => (await apiService.getEvent(eventId as string)).data as EventDetail,
    enabled: !!eventId,
  });
  const event = query.data;

  const schema = useMemo(() => z.object({
    name: z.string().trim().min(1, t('eventSettings.errors.nameRequired')),
    event_type: z.enum(['crossfit', 'course']),
    description: z.string(),
    date_start: z.string().min(1, t('eventSettings.errors.dateRequired')),
    date_end: z.string().min(1, t('eventSettings.errors.dateRequired')),
    venue_name: z.string(),
    location_street: z.string(),
    location_postal_code: z.string(),
    location_city: z.string(),
    location_country: z.string(),
    status: z.string(),
    visibility: z.enum(['public', 'private', 'unlisted']),
  }).refine((v) => !v.date_start || !v.date_end || v.date_end >= v.date_start, {
    path: ['date_end'],
    message: t('eventSettings.errors.endBeforeStart'),
  }), [t]);

  // `values` et non un reset dans un effet : les listes déroulantes (statut,
  // visibilité) montées avant le reset restaient VIDES, et le formulaire
  // refusait ensuite d'enregistrer un statut indéfini — sans rien afficher.
  // Les listes déroulantes Radix, dans un <form>, émettent une valeur VIDE au
  // montage (select natif caché) : elle effaçait le statut et le type et
  // comptait deux « modifications ». Aucune option n'est vide, on l'ignore.
  const loadedValues = useMemo(() => (event ? toValues(event) : undefined), [event]);
  const form = useForm<Values>({ resolver: zodResolver(schema), mode: 'onTouched', values: loadedValues });

  const isDirty = form.formState.isDirty;
  const dirtyCount = Object.keys(form.formState.dirtyFields).length;

  // Quitter la page (fermer l'onglet, recharger) avec des changements en
  // attente demande confirmation au navigateur.
  useEffect(() => {
    if (!isDirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');
  const [resultsNotify, setResultsNotify] = useState<{ recipients: number; resultsUrl: string | null } | null>(null);
  const [notifying, setNotifying] = useState(false);

  const save = async (values: Values) => {
    if (!eventId) return;
    // Une date à moitié tapée a une valeur VIDE pour React ; le navigateur,
    // lui, sait qu'elle est incomplète (validity.badInput).
    if (refuseIfDateInputIncomplete(t)) return;
    setSaving(true);
    setServerError('');
    try {
      const res = await apiService.updateEvent(eventId, values);
      toast.success(t('eventSettings.saved'));
      form.reset(values);
      qc.invalidateQueries({ queryKey: ['event', eventId] });
      qc.invalidateQueries({ queryKey: overviewKey(eventId) });
      refreshEvents();
      // Terminer l'événement referme les vagues restées « en cours » (2026-09-27) : on le dit.
      const closed = res?.data?.heats_closed;
      if (closed > 0) toast.info(t('eventSettings.heatsClosed', { count: closed }));
      const pending = res?.data?.pending_results_notification;
      // Passage en terminé : on PROPOSE de prévenir, jamais automatiquement.
      if (pending && pending.recipients > 0) setResultsNotify({ recipients: pending.recipients, resultsUrl: pending.results_url });
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setServerError(typeof detail === 'string' ? detail : t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const notifyResults = async () => {
    if (!eventId) return;
    setNotifying(true);
    try {
      const res = await apiService.notifyResults(eventId);
      toast.success(t('events.resultsNotify.sent', { count: res.data.notified_count }));
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setNotifying(false);
      setResultsNotify(null);
    }
  };

  // ── Invitations (événement privé, déjà enregistré comme tel) ─────────────
  const savedPrivate = event?.visibility === 'private';
  const invitationsQuery = useQuery({
    queryKey: ['event-invitations', eventId],
    queryFn: async () => (await apiService.getEventInvitations(eventId as string)).invitations as { id: string; email: string }[],
    enabled: !!eventId && savedPrivate && !!features?.private_events,
  });
  const [newEmails, setNewEmails] = useState('');
  const parsedEmails = newEmails.split(/[,;\s\n]+/).map((e) => e.trim()).filter(Boolean);
  const [confirmInvite, setConfirmInvite] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const invite = async () => {
    if (!eventId || !parsedEmails.length) return;
    setInviting(true);
    try {
      const res = await apiService.addEventInvitations(eventId, parsedEmails);
      toast.success(t('eventSettings.invitationsSent', { count: res?.added?.length ?? parsedEmails.length }));
      setNewEmails('');
      qc.invalidateQueries({ queryKey: ['event-invitations', eventId] });
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setInviting(false);
      setConfirmInvite(false);
    }
  };

  const removeInvitation = async (email: string) => {
    if (!eventId) return;
    try {
      await apiService.removeEventInvitation(eventId, email);
      qc.invalidateQueries({ queryKey: ['event-invitations', eventId] });
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    }
  };

  // ── Logo : s'applique tout de suite, comme avant (fichier ≠ formulaire) ──
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoWarnings, setLogoWarnings] = useState<string[]>([]);
  useEffect(() => { setLogoUrl(event?.logo_url || null); }, [event?.logo_url]);

  const uploadLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !eventId) return;
    setLogoError(null);
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      setLogoError(t('events.logo.invalidType', 'Format non supporté (PNG, JPEG, WebP, SVG)'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoError(t('events.logo.tooLarge', 'Fichier trop volumineux (max 2 Mo)'));
      return;
    }
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiService.post(`/events/${eventId}/logo`, fd);
      setLogoUrl(res.data.logo_url);
      setLogoWarnings(res.data.warnings || []);
      refreshEvents();
    } catch (err: any) {
      setLogoWarnings([]);
      setLogoError(err.response?.data?.detail || t('events.logo.uploadError', "Échec de l'upload du logo"));
    } finally {
      setLogoBusy(false);
      e.target.value = '';
    }
  };

  const deleteLogo = async () => {
    if (!eventId) return;
    setLogoBusy(true);
    setLogoError(null);
    try {
      await apiService.delete(`/events/${eventId}/logo`);
      setLogoUrl(null);
      setLogoWarnings([]);
      refreshEvents();
    } catch (err: any) {
      setLogoError(err.response?.data?.detail || t('events.logo.deleteError', 'Échec de la suppression'));
    } finally {
      setLogoBusy(false);
    }
  };

  // ── Rendu ────────────────────────────────────────────────────────────────
  const visibility = form.watch('visibility');
  const showInvitations = !!features?.private_events && (savedPrivate || visibility === 'private');
  const sections: SectionId[] = SECTIONS.filter((s) => s !== 'invitations' || showInvitations);
  const publicUrl = event?.slug ? `${PUBLIC_SITE_URL}/events/${event.slug}` : null;

  if (query.isError) {
    return (
      <div className="p-4 md:p-8">
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-4">
            {t('eventSettings.loadError')}
            <Button variant="outline" size="sm" onClick={() => query.refetch()}>{t('common.retry')}</Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4 pb-0 md:p-8 md:pb-0">
      <PageHeader
        eyebrow={t('eventSettings.eyebrow')}
        title={event?.name ?? <Skeleton className="h-8 w-72" />}
        actions={
          <>
            {publicUrl && (
              <Button variant="ghost" size="sm" asChild>
                <a href={publicUrl} target="_blank" rel="noopener noreferrer">{t('nav.viewPublicPage')}<ExternalLink size={14} /></a>
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/overview')}>{t('nav.overview')}</Button>
          </>
        }
      />

      <div className="mt-8 grid gap-10 lg:grid-cols-[180px_minmax(0,1fr)]">
        {/* Sommaire : une ancre par section, collé en haut au défilement. */}
        <nav aria-label={t('eventSettings.sectionsNav')} className="hidden lg:block">
          <ul className="sticky top-24 space-y-1 text-sm">
            {sections.map((s) => (
              <li key={s}>
                <a href={`#settings-${s}`} className="block rounded-md px-3 py-1.5 text-muted-foreground no-underline hover:bg-white/[0.04] hover:text-foreground">
                  {t(`eventSettings.sections.${s}`)}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {query.isLoading || !event ? (
          <div className="max-w-3xl space-y-6">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : (
          <Form {...form}>
            <form id="event-settings-form" onSubmit={form.handleSubmit(save)} noValidate className="max-w-3xl space-y-10">
              <Section id="info" title={t('eventSettings.sections.info')}>
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('events.eventName')}</FormLabel>
                    <FormControl><Input {...field} placeholder={t('events.namePlaceholder')} /></FormControl>
                    <FormDescription>{t('eventSettings.nameHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="event_type" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('events.eventType')}</FormLabel>
                    <Select value={field.value} onValueChange={(v) => { if (v) field.onChange(v); }}>
                      <FormControl><SelectTrigger className="sm:w-64"><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="crossfit">{t('events.crossfit')}</SelectItem>
                        <SelectItem value="course">{t('events.course')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )} />
                <FormField control={form.control} name="description" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.description')}</FormLabel>
                    <FormControl><Textarea rows={6} {...field} placeholder={t('events.descriptionPlaceholder')} /></FormControl>
                    <FormDescription>{t('eventSettings.descriptionHint')}</FormDescription>
                  </FormItem>
                )} />
              </Section>

              <Section id="place" title={t('eventSettings.sections.place')}>
                <div className="grid gap-5 sm:grid-cols-2">
                  <FormField control={form.control} name="date_start" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('events.startDate')}</FormLabel>
                      <FormControl><DateTimeInput type="date" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="date_end" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('events.endDate')}</FormLabel>
                      <FormControl><DateTimeInput type="date" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <FormField control={form.control} name="venue_name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('events.venueName')}</FormLabel>
                    <FormControl><Input {...field} placeholder={t('events.venueNamePlaceholder')} /></FormControl>
                    <FormDescription>{t('events.venueNameHint')}</FormDescription>
                  </FormItem>
                )} />
                <FormField control={form.control} name="location_street" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('events.locationStreet')}</FormLabel>
                    <FormControl><Input {...field} placeholder={t('events.locationStreetPlaceholder')} /></FormControl>
                  </FormItem>
                )} />
                <div className="grid gap-5 sm:grid-cols-[140px_minmax(0,1fr)_minmax(0,1fr)]">
                  <FormField control={form.control} name="location_postal_code" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('events.postalCode')}</FormLabel>
                      <FormControl><Input {...field} placeholder="1000" /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="location_city" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('events.city')}</FormLabel>
                      <FormControl><Input {...field} placeholder={t('events.cityPlaceholder')} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="location_country" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('events.country')}</FormLabel>
                      <FormControl><Input {...field} placeholder={t('events.countryPlaceholder')} /></FormControl>
                    </FormItem>
                  )} />
                </div>
              </Section>

              <Section id="publication" title={t('eventSettings.sections.publication')} hint={t('eventSettings.publicationHint')}>
                <FormField control={form.control} name="status" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.status')}</FormLabel>
                    <Select value={field.value} onValueChange={(v) => { if (v) field.onChange(v); }}>
                      <FormControl><SelectTrigger className="sm:w-64"><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="draft">{t('common.draft')}</SelectItem>
                        <SelectItem value="published">{t('common.published')}</SelectItem>
                        <SelectItem value="active">{t('common.active')}</SelectItem>
                        <SelectItem value="completed">{t('events.completed')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>{t(`eventSettings.statusHint.${field.value}`, { defaultValue: '' })}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
                {features?.private_events && (
                  <FormField control={form.control} name="visibility" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('events.visibility')}</FormLabel>
                      <Select value={field.value} onValueChange={(v) => { if (v) field.onChange(v); }}>
                        <FormControl><SelectTrigger className="sm:w-64"><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="public">{t('events.visibilityPublic')}</SelectItem>
                          <SelectItem value="private">{t('events.visibilityPrivate')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {field.value === 'private' ? t('events.visibilityPrivateHint') : t('events.visibilityPublicHint')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )} />
                )}
              </Section>

              {showInvitations && (
                <Section id="invitations" title={t('eventSettings.sections.invitations')} hint={t('events.invitationsInfo')}>
                  {!savedPrivate ? (
                    <p className="text-sm text-muted-foreground">{t('eventSettings.saveBeforeInviting')}</p>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <label htmlFor="new-invitations" className="text-sm text-muted-foreground">{t('events.addInvitees')}</label>
                        <Textarea
                          id="new-invitations"
                          data-email
                          value={newEmails}
                          onChange={(e) => setNewEmails(e.target.value)}
                          placeholder={t('events.inviteesPlaceholder')}
                          rows={3}
                        />
                        <div className="flex flex-wrap items-center gap-3">
                          <Button type="button" variant="outline" size="sm" disabled={!parsedEmails.length || inviting} onClick={() => setConfirmInvite(true)}>
                            {t('eventSettings.sendInvitations', { count: parsedEmails.length })}
                          </Button>
                          <span className="text-xs text-muted-foreground">{t('eventSettings.invitationsSendNow')}</span>
                        </div>
                      </div>

                      <div>
                        <div className="mb-2 text-sm text-muted-foreground">
                          {t('eventSettings.invitedCount', { count: invitationsQuery.data?.length ?? 0 })}
                        </div>
                        {invitationsQuery.isLoading ? (
                          <Skeleton className="h-10 w-full" />
                        ) : (invitationsQuery.data?.length ?? 0) > 0 ? (
                          <ul className="max-h-72 overflow-y-auto">
                            {[...(invitationsQuery.data ?? [])].sort((a, b) => a.email.localeCompare(b.email)).map((inv) => (
                              <li key={inv.id} className="group flex items-center justify-between border-b border-white/[0.06] py-2 text-sm">
                                <span className="truncate">{inv.email}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground [@media(hover:hover)]:opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                                  aria-label={t('eventSettings.removeInvitation', { email: inv.email })}
                                  onClick={() => removeInvitation(inv.email)}
                                >
                                  <X size={14} />
                                </Button>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-muted-foreground">{t('events.noInvitees')}</p>
                        )}
                      </div>

                      {publicUrl && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(publicUrl);
                            setLinkCopied(true);
                            setTimeout(() => setLinkCopied(false), 2000);
                          }}
                        >
                          {linkCopied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
                          {linkCopied ? t('events.linkCopied') : t('events.copyLink')}
                        </Button>
                      )}
                    </>
                  )}
                </Section>
              )}

              <Section id="logo" title={t('eventSettings.sections.logo')} hint={t('events.logo.help', "Affiché sur la vidéo/image de partage. S'il est vide, le logo de l'organisateur est utilisé.")}>
                <div className="flex flex-wrap items-center gap-4">
                  {logoUrl ? (
                    <img src={logoFullUrl(logoUrl) || ''} alt={t('eventSettings.logoAlt')} className="h-16 w-auto max-w-[180px] rounded bg-black/20 object-contain p-1" />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded bg-muted text-xs text-muted-foreground">{t('events.logo.none', 'Aucun')}</div>
                  )}
                  <label className={cn('inline-flex cursor-pointer items-center rounded-md border px-3 py-2 text-sm hover:bg-white/[0.04]', logoBusy && 'pointer-events-none opacity-60')}>
                    {logoBusy ? <Loader2 size={14} className="animate-spin" /> : t('events.logo.upload', 'Choisir un logo')}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" disabled={logoBusy} onChange={uploadLogo} />
                  </label>
                  {logoUrl && (
                    <Button type="button" variant="ghost" size="sm" disabled={logoBusy} onClick={deleteLogo}>{t('events.logo.delete', 'Retirer')}</Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{t('eventSettings.logoImmediate')}</p>
                {logoError && <p className="text-xs text-destructive">{logoError}</p>}
                <LogoWarnings codes={logoWarnings} />
              </Section>

              {serverError && (
                <Alert variant="destructive"><AlertDescription>{serverError}</AlertDescription></Alert>
              )}
            </form>
          </Form>
        )}
      </div>

      {/* Barre d'enregistrement, toujours visible : la page est longue et
          l'état « non enregistré » doit se lire où qu'on soit. */}
      {event && (
        <div className="sticky bottom-0 z-30 -mx-4 mt-10 border-t border-white/[0.08] bg-background/95 backdrop-blur md:-mx-8">
          <div className="flex items-center gap-3 px-4 py-3 md:px-8">
            <span className="flex flex-1 items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
              {isDirty && (
                <>
                  <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-warning" />
                  {t('teams.form.unsaved', { count: Math.max(dirtyCount, 1) })}
                </>
              )}
            </span>
            <Button type="button" variant="ghost" disabled={!isDirty || saving} onClick={() => form.reset(toValues(event))}>
              {t('eventSettings.discard')}
            </Button>
            <Button type="submit" form="event-settings-form" disabled={!isDirty || saving}>
              {saving && <Loader2 className="animate-spin" />}
              {t('common.save')}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmInvite}
        onOpenChange={(o) => { if (!o && !inviting) setConfirmInvite(false); }}
        title={t('eventSettings.confirmInviteTitle', { count: parsedEmails.length })}
        message={t('eventSettings.confirmInviteMessage', { count: parsedEmails.length })}
        confirmLabel={t('eventSettings.sendInvitations', { count: parsedEmails.length })}
        loading={inviting}
        onConfirm={invite}
      />

      <Dialog open={!!resultsNotify} onOpenChange={(o) => { if (!o && !notifying) setResultsNotify(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{t('events.resultsNotify.title')}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">{t('events.resultsNotify.intro', { count: resultsNotify?.recipients ?? 0 })}</p>
            {resultsNotify?.resultsUrl && (
              <a href={resultsNotify.resultsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-foreground hover:underline">
                {t('events.resultsNotify.checkLink')} <ExternalLink size={14} />
              </a>
            )}
            <p className="text-xs text-muted-foreground">{t('events.resultsNotify.hint')}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResultsNotify(null)} disabled={notifying}>{t('common.cancel')}</Button>
            <Button onClick={notifyResults} disabled={notifying}>
              {notifying && <Loader2 className="animate-spin" />}
              {t('events.resultsNotify.confirm', { count: resultsNotify?.recipients ?? 0 })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventSettings;
